import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { createPost, CATEGORIES, WORKS_IN } from '@/lib/exchangeApi';

const inputCls =
  'w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors';

const Label = ({ children, required }) => (
  <label className="block text-sm font-semibold text-ink mb-1.5">
    {children} {required && <span className="text-brand-orange">*</span>}
  </label>
);

const SubmitModal = ({ onClose, onPublished }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [steps, setSteps] = useState(['', '', '']);
  const [worksIn, setWorksIn] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [organization, setOrganization] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const valid =
    title.trim() && category && description.trim() && prompt.trim() &&
    displayName.trim() && organization.trim();

  const toggleWorksIn = (w) =>
    setWorksIn((prev) => (prev.includes(w) ? prev.filter((x) => x !== w) : [...prev, w]));

  const setStep = (i, v) => setSteps((prev) => prev.map((s, idx) => (idx === i ? v : s)));

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const post = await createPost({
        title: title.trim(),
        category,
        description: description.trim(),
        prompt: prompt.trim(),
        how_to_use: steps.map((s) => s.trim()).filter(Boolean),
        works_in: worksIn,
        display_name: displayName.trim(),
        organization: organization.trim(),
      });
      onPublished(post);
    } catch {
      setError('Could not publish — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-ink/40 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
      data-testid="submit-modal-overlay"
    >
      <div
        className="w-full max-w-xl bg-cream rounded-3xl shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="submit-modal"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/[0.07] sticky top-0 bg-cream rounded-t-3xl z-10">
          <h2 className="text-xl font-bold text-ink">Share a prompt</h2>
          <button
            onClick={onClose}
            data-testid="submit-modal-close"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
          >
            <X size={18} className="text-ink-secondary" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <Label required>Title</Label>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Donor thank-you in your org's voice" data-testid="submit-title" />
          </div>

          <div>
            <Label required>Category</Label>
            <div className="flex flex-wrap gap-2" data-testid="submit-category">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`submit-category-${c}`}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border-2 transition-colors ${
                    category === c
                      ? 'bg-brand-teal border-brand-teal text-white'
                      : 'bg-white border-black/10 text-ink-secondary hover:border-brand-teal/50'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label required>What it does</Label>
            <textarea className={`${inputCls} resize-y`} rows={2} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One or two sentences on what this prompt produces." data-testid="submit-description" />
          </div>

          <div>
            <Label required>The prompt</Label>
            <textarea className={`${inputCls} font-mono text-[13px] resize-y`} rows={6} value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Paste the full prompt here." data-testid="submit-prompt" />
          </div>

          <div>
            <Label>How to use it <span className="font-normal text-ink-muted">(optional)</span></Label>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-brand-teal/10 text-brand-teal text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <input className={inputCls} value={s} onChange={(e) => setStep(i, e.target.value)}
                    placeholder={`Step ${i + 1}`} data-testid={`submit-step-${i + 1}`} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Works in</Label>
            <div className="flex flex-wrap gap-2" data-testid="submit-works-in">
              {WORKS_IN.map((w) => (
                <button
                  key={w}
                  onClick={() => toggleWorksIn(w)}
                  data-testid={`submit-works-in-${w}`}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border-2 transition-colors ${
                    worksIn.includes(w)
                      ? 'bg-brand-orange border-brand-orange text-white'
                      : 'bg-white border-black/10 text-ink-secondary hover:border-brand-orange/50'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label required>Display name</Label>
              <input className={inputCls} value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Priya" data-testid="submit-display-name" />
            </div>
            <div>
              <Label required>Organization</Label>
              <input className={inputCls} value={organization} onChange={(e) => setOrganization(e.target.value)}
                placeholder="e.g. Roshni Foundation" data-testid="submit-organization" />
            </div>
          </div>

          <p className="text-xs text-ink-muted">Your name and org appear on the post. No account is created.</p>

          {error && <p className="text-sm text-red-600" data-testid="submit-error">{error}</p>}
        </div>

        <div className="px-6 py-5 border-t border-black/[0.07] flex justify-end gap-3 sticky bottom-0 bg-cream rounded-b-3xl">
          <button onClick={onClose} data-testid="submit-cancel"
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-ink-secondary hover:text-ink transition-colors">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!valid || submitting}
            data-testid="submit-publish"
            className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${
              valid && !submitting
                ? 'bg-brand-orange text-white hover:bg-brand-orange-hover'
                : 'bg-black/10 text-ink-muted cursor-not-allowed'
            }`}
          >
            {submitting && <Loader2 size={15} className="animate-spin" />}
            {submitting ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitModal;
